import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import SummaryCard from '@acorn-portal/shared/src/components/card/SummaryCard';
import AddressDisplay from '@acorn-portal/shared/src/components/display/address';
import DateDisplay from '@acorn-portal/shared/src/components/display/date';
import DriverExcess from '@acorn-portal/shared/src/components/display/driver-excess';
import Drivers from '@acorn-portal/shared/src/components/display/drivers';
import Endorsements from '@acorn-portal/shared/src/components/display/endorsements';
import Price from '@acorn-portal/shared/src/components/display/price';
import UsageAndMileage from '@acorn-portal/shared/src/components/display/usage-and-milage';
import Vehicle from '@acorn-portal/shared/src/components/display/vehicle';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useReferenceData from '@acorn-portal/shared/src/hooks/useReferenceData';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import { CoverLevel, Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { ListNames } from '@acorn-portal/shared/src/store/referenceData.slice';
import { clearRenewalState, updateFutureDatedRenewal } from '@acorn-portal/shared/src/store/renewal.slice';
import { bypassComplexRenewal, formatCurrency, formatPounds } from '@acorn-portal/shared/src/utils';
import { VOLUNTARY_EXCESS_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { CheckCircle, CircleOutlined } from '@mui/icons-material';
import { Box, Checkbox, Divider, FormControlLabel, Grid, styled, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';

import PostalDocuments from '@acorn-portal/shared/src/components/content/postal-documents';
import DemandsNeeds from '@acorn-portal/shared/src/components/display/demands-needs';
import ErrorPage from '@acorn-portal/shared/src/pages/misc/ErrorPage';
import { resetRenewalChanges, updateChangeOfExcess } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';
import { isDate, parseISO } from 'date-fns';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const CopyText = styled(Typography)({
	textAlign: 'left',
	marginBottom: 4,
	marginTop: 4,
});

interface ISummaryPriceProps {
	totalAmountPayable: number | undefined;
	renewalStartDate: string | undefined;
	fee?: number;
}

const SummaryPrice = ({ totalAmountPayable, renewalStartDate, fee }: ISummaryPriceProps) => {
	return (
		<Grid
			container
			rowSpacing={2}
			columnSpacing={2}
			gridAutoRows='1fr'
			sx={{ flexDirection: { xs: 'column', md: 'row' }, mb: 2, width: '100%' }}
		>
			<Grid size={{ sm: 6 }}>
				<Box
					sx={{
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						borderRadius: 2,
						height: '100%',
						width: '100%',
					}}
				>
					{renewalStartDate && <DateDisplay date={renewalStartDate} description='This change starts' />}
				</Box>
			</Grid>
			<Grid size={{ sm: 6 }}>
				<Box
					sx={(theme) => ({
						border: 1,
						borderColor: 'grey.300',
						p: 2,
						borderRadius: 2,
						textAlign: 'right',
						[theme.breakpoints.down('md')]: {
							textAlign: 'left',
						},
					})}
				>
					{totalAmountPayable && <Price color='primary.main' value={totalAmountPayable} description='Amount Payable' />}
					<Typography fontSize={12} sx={{ paddingTop: 5 }}>
						Inclusive of {import.meta.env.VITE_IPT_PERCENTAGE}% Insurance Premium Tax
					</Typography>
					{fee && <Typography fontSize={12}>{formatCurrency(fee)} non-refundable administration fee</Typography>}
				</Box>
			</Grid>
		</Grid>
	);
};

export interface IRenewalSummaryForm {
	voluntaryExcess: string;
	renewalDate: IDateObject;
	minYear: number;
	minMonth: number;
}

interface IDateObject {
	day: string;
	month: string;
	year: string;
	hour: string;
	minute: string;
}

const validationSchema = Yup.object().shape({
	voluntaryExcess: Yup.string().required('Please select your level of voluntary excess'),
	renewalDate: Yup.object().shape({
		day: Yup.string().notRequired(),
		month: Yup.string().notRequired(),
		year: Yup.string().notRequired(),
	}),
});

/*
 * Requires full policy details and renewal rating to be populated in Redux prior to rendering
 */
const RenewalSummaryScreenPage: FC = () => {
	const dispatch = useAppDispatch();
	const [confirmChanges, setConfirmChanges] = useState(false);
	const [open, toggleModal] = useState(false);
	const { getReferenceDataValue } = useReferenceData();
	const renewalState = useAppSelector((redux) => redux.renewal);
	const lists = useAppSelector((redux) => redux.referenceData);
	const renewalChanges = useAppSelector((redux) => redux.renewalChanges);
	const [loadingModal, setLoadingModal] = useState(false);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const enableComplexRenewal = !bypassComplexRenewal(fullPolicy.product);

	const { rateRenewal } = useRateRenewal();

	const isRenewalDataMissing = !renewalState.renewalRatingResponse?.annualPremium;
	const isTaxi = fullPolicy.product === Product.TAXI;

	const confirmationPolicyWording =
		fullPolicy.product === Product.TELEMATICS
			? 'Please be aware any changes you make to your policy may influence your policy terms, such as key endorsements and excesses on the policy. If you go ahead with this renewal, this will be communicated to you in your policy documents. Please note your Renewal has been quoted on the basis that it will include a telematics device.'
			: 'Please be aware any changes you make to your policy may influence your policy terms, such as key endorsements and excesses on the policy. If you go ahead with this renewal, this will be communicated to you in your policy documents.';
	const navigate = useNavigate();

	const handleDiscard = () => {
		setLoadingModal(true);
		dispatch(clearRenewalState());
		dispatch(resetRenewalChanges());
		setLoadingModal(false);
		navigate(ROUTE_PATHS.home);
	};

	const handleExcessChange = async (excess: string) => {
		setLoadingModal(true);
		dispatch(updateChangeOfExcess(true));
		await rateRenewal({ renewalState, fullPolicy, excess });
		setLoadingModal(false);
	};

	const getFormDate = (value?: string | null) => {
		if (!value || value === null) {
			return {
				day: '',
				month: '',
				year: '',
				hour: '',
				minute: '',
			};
		}
		const tempDate = parseISO(value);
		const year = tempDate.getFullYear();
		const month = tempDate.getMonth();
		const day = tempDate.getDate();
		const hour = tempDate.getHours();
		const minute = tempDate.getMinutes();

		function minTwoDigits(n: number) {
			return (n < 10 ? '0' : '') + n.toString();
		}

		return {
			year: year.toString(),
			month: minTwoDigits(month + 1),
			day: minTwoDigits(day),
			hour: minTwoDigits(hour),
			minute: minTwoDigits(minute),
		};
	};

	const onSubmit = async (): Promise<void> => {
		dispatch(
			updateFutureDatedRenewal({
				isFutureDatedRenewal: false,
				futureRenewalStartDate: undefined,
			}),
		);
		const tempDate = parseISO(renewalState.renewalStartDate);
		const result = isDate(tempDate);
		if (!result) return;
		navigate(`${ROUTE_PATHS.policyRenewals}/payment`);
	};

	// Check if renewal data exists in Redux - return early if missing
	if (isRenewalDataMissing) {
		return <ErrorPage />;
	}

	// Compute initial values only after confirming data exists
	const initialValues: IRenewalSummaryForm = {
		voluntaryExcess: renewalState.renewalRatingResponse?.coverDetails?.voluntaryExcess as unknown as string,
		renewalDate: getFormDate(renewalState.renewalStartDate),
		minYear: getFormDate(renewalState.renewalStartDate).year as unknown as number,
		minMonth: getFormDate(renewalState.renewalStartDate).month as unknown as number,
	};

	return (
		<>
			<Formik<IRenewalSummaryForm>
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={onSubmit}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							<JourneyCardLayout pageTitle='Renewal Summary' loading={false}>
								<Grid display='flex' flexDirection='column' sx={{ alignItems: { sm: 'center' } }}>
									<Grid container flexDirection='column' sx={{ pb: 4, maxWidth: '800px', width: '100%' }} gap={2}>
										{enableComplexRenewal ? (
											<Typography sx={{ fontSize: 16, paddingBottom: 4 }}>
												Please review your renewal quote details below and if you are happy with your details click
												Continue. You can update your renewal details by clicking the amend buttons on the relevant
												section. <b>Please note that any changes you make will only apply to your renewal policy</b>. If
												you wish to make a change to your current policy or have any changes to your motoring claims or
												convictions{' '}
												{!fullPolicy.scheme?.includes('Motorcade') && (
													<>
														please contact one of our agents on{' '}
														<Typography
															component='a'
															href={`tel:${getPhoneNumber(fullPolicy.product, 'CONTACT')}`}
															color='primary'
															fontWeight='bold'
														>
															{getPhoneNumber(fullPolicy.product, 'CONTACT')}
														</Typography>{' '}
														who will be happy to assist.
													</>
												)}
												{fullPolicy.scheme?.includes('Motorcade') && (
													<>
														please contact us using the webchat facility on our{' '}
														<Typography component='a' href='https://motorcade.co.uk/' color='primary' fontWeight='bold'>
															main website
														</Typography>
													</>
												)}
											</Typography>
										) : (
											<Typography sx={{ fontSize: 16, paddingBottom: 4 }}>
												Please review your renewal quote details below and if you are happy with your details click
												Continue. If you wish to make a change to your current policy or make a change to your renewal{' '}
												{!fullPolicy.scheme?.includes('Motorcade') && (
													<>
														please contact one of our agents on{' '}
														<Typography
															component='a'
															href={`tel:${getPhoneNumber(fullPolicy.product, 'CONTACT')}`}
															color='primary'
															fontWeight='bold'
														>
															{getPhoneNumber(fullPolicy.product, 'CONTACT')}
														</Typography>{' '}
														who will be happy to assist.
													</>
												)}
												{fullPolicy.scheme?.includes('Motorcade') && (
													<>
														please use our live chat service to contact one of our agents who will be happy to assist.
													</>
												)}
											</Typography>
										)}
										<SummaryPrice
											renewalStartDate={renewalState.futureRenewalStartDate ?? renewalState.renewalStartDate}
											totalAmountPayable={renewalState.renewalRatingResponse?.annualPremium}
											fee={renewalState.renewalRatingResponse?.policyAdministrationFee}
										/>
										<SummaryCard
											title='Vehicle'
											isUpdated={renewalChanges.changeOfVehicle}
											amendLink={ROUTE_PATHS.renewalMakeAChangeVehicle}
											showAmendButton={enableComplexRenewal}
										>
											<Vehicle
												isSummaryScreen
												product={fullPolicy.product}
												vehicle={{
													...renewalState.vehicleDetails,
													registrationNumber: renewalState.vehicleDetails.registration,
													vehicleUse: '',
												}}
											>
												<Typography>Estimated value: {formatPounds(renewalState.vehicleDetails.value)}</Typography>
												<Typography sx={{ display: 'flex', alignItems: 'start' }}>
													{getReferenceDataValue(
														ListNames.levelOfCoverTypes,
														renewalState.vehicleDetails.coverDefinitionCode ?? '',
													)}
												</Typography>
											</Vehicle>
										</SummaryCard>
										<SummaryCard
											title='Address'
											isUpdated={renewalChanges.changeOfAddress}
											showAmendButton={enableComplexRenewal}
											amendLink={ROUTE_PATHS.renewalMakeAChangeAddress}
										>
											<AddressDisplay
												address={{
													...renewalState.policyDetails.policyholder.address,
													postcode: renewalState.policyDetails.policyholder.address.postcode,
												}}
												overnightAddress={
													renewalState.vehicleDetails.overnightAddress ?? renewalState.vehicleDetails.overnightAddress
												}
											/>
										</SummaryCard>
										<SummaryCard
											title='Drivers'
											isUpdated={renewalChanges.changeOfDriver}
											showAmendButton={enableComplexRenewal}
											amendLink={ROUTE_PATHS.renewalMakeAChangeDrivers}
										>
											<Drivers
												drivers={renewalState.drivers.map((driver) => ({
													...driver,
													dataPointId: driver.dataPointId ?? '',
													lastName: driver.lastNames,
													isPolicyholder: false,
													mainDriver: driver.isMainDriver,
												}))}
											/>
										</SummaryCard>
										<SummaryCard
											isUpdated={renewalChanges.changeOfUsage}
											title={isTaxi ? 'Usage' : 'Usage and Mileage'}
											showAmendButton={enableComplexRenewal}
											amendLink={
												isTaxi ? ROUTE_PATHS.renewalMakeAChangeUsageTaxi : ROUTE_PATHS.renewalMakeAChangeUsageAndMileage
											}
										>
											<UsageAndMileage
												vehicleUse={renewalState.vehicleDetails.vehicleUse}
												annualMileage={renewalState.vehicleDetails.annualMileage}
												businessMileage={renewalState.vehicleDetails.businessMileage}
												vehicleCover={renewalState.vehicleDetails.coverDefinitionCode}
												licencingAuthority={renewalState.vehicleDetails.licencingAuthority}
												operatingArea={renewalState.vehicleDetails.operatingArea}
												product={fullPolicy.product}
											/>
										</SummaryCard>
										{renewalState.vehicleDetails.coverDefinitionCode !== 'TPO' && (
											<>
												<SummaryCard
													title={'Voluntary Excess (£)'}
													showAmendButton={false}
													isUpdated={renewalChanges.changeOfExcess}
												>
													<SelectFormField
														fillWidth
														label='Voluntary Excess (£)'
														labelVariant='desktopHidden'
														name='voluntaryExcess'
														id='voluntaryExcess'
														value={formik.values.voluntaryExcess}
														onChange={async (value) => {
															await handleExcessChange(value);
															formik.setFieldValue('voluntaryExcess', value);
														}}
														options={lists.voluntaryExcessOptions as IOptions[]}
														error={formik.touched.voluntaryExcess && Boolean(formik.errors.voluntaryExcess)}
														helperText={formik.touched.voluntaryExcess && formik.errors.voluntaryExcess}
														modal={{
															title: VOLUNTARY_EXCESS_MODAL.header,
															description: VOLUNTARY_EXCESS_MODAL.text,
														}}
														isDisabled={bypassComplexRenewal(fullPolicy.product)}
													/>
												</SummaryCard>
												{renewalState.renewalRatingResponse?.coverDetails?.coverLevel !== CoverLevel.TPO && (
													<Box sx={{ width: '100%', py: 2 }}>
														<Typography fontWeight='bold'>Policy Excess</Typography>
														<Grid container alignItems='center' sx={{ py: 2 }} gap={2}>
															<Grid flex={1}>
																<Typography>
																	<Price
																		value={
																			renewalState.renewalRatingResponse?.coverDetails
																				?.voluntaryExcess as unknown as number
																		}
																		description='Voluntary Excess'
																	/>
																</Typography>
															</Grid>
															<Grid>
																<Price
																	value={
																		renewalState.renewalRatingResponse?.coverDetails
																			?.compulsoryExcess as unknown as number
																	}
																	description='Compulsory Excess'
																/>
															</Grid>
														</Grid>
													</Box>
												)}
												{renewalState.drivers && (
													<DriverExcess
														drivers={renewalState.drivers.map((driver) => ({
															...driver,
															dataPointId: driver.dataPointId ?? '',
															lastName: driver.lastNames,
															isPolicyholder: driver.relationshipToProposer === 'P',
															mainDriver: driver.isMainDriver,
														}))}
														driverExcesses={renewalState.renewalRatingResponse?.coverDetails?.driverExcesses}
													/>
												)}
											</>
										)}
										<Grid>
											<Divider sx={{ mb: 4 }} />
											<DemandsNeeds
												coverLevel={renewalState.vehicleDetails.coverDefinitionCode as CoverLevel}
												product={fullPolicy.product}
											/>
											<Endorsements
												endorsements={renewalState.renewalRatingResponse?.coverDetails?.endorsements || []}
											/>
										</Grid>
										<Divider />
										<Box
											sx={(theme) => ({
												mx: 0,
												my: 2,
												[theme.breakpoints.down('md')]: { mx: 'unset' },
											})}
										>
											<CopyText>{confirmationPolicyWording}</CopyText>
											<PostalDocuments />
											<FormControlLabel
												control={
													<Checkbox
														id='confirm'
														name='confirm'
														slotProps={{
															input: {
																'aria-label': 'confirm',
															},
														}}
														icon={<CircleOutlined />}
														checkedIcon={<CheckCircle />}
														sx={{ '& .MuiSvgIcon-root': { fontSize: '3rem' } }}
														value={confirmChanges}
														onChange={() => setConfirmChanges(!confirmChanges)}
													/>
												}
												labelPlacement='start'
												label='I confirm all the details above are correct.'
												sx={{ margin: 0 }}
											/>
										</Box>
									</Grid>
								</Grid>

								<ActionButtons>
									<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
									<ContinueButton
										onClick={() => {
											formik.handleSubmit();
										}}
										disabled={!confirmChanges}
									>
										Continue
									</ContinueButton>
								</ActionButtons>
								<HelperModal
									open={open}
									onClose={() => {
										toggleModal(false);
									}}
									title='Discard The Changes?'
									description='Are you sure you wish to discard the changes?'
									closeText='No'
									submitText='Yes'
									onSubmit={handleDiscard}
								/>
							</JourneyCardLayout>
						</Form>
					);
				}}
			</Formik>
			<LoadingModal open={loadingModal} message='Updating Quote...' />
		</>
	);
};

export default RenewalSummaryScreenPage;
